import { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
 Avatar,
 Box,
 CloseButton,
 Collapse,
 Flex,
 IconButton,
 Text,
 Image,
 Drawer,
 DrawerContent,
 useDisclosure,
} from '@chakra-ui/react';
import {
 FiActivity,
 FiBarChart2,
 FiBookOpen,
 FiChevronDown,
 FiFolderPlus,
 FiLogOut,
 FiTrendingUp,
} from 'react-icons/fi';
import LogoMobile from './LogoTopbar';
import NavItem from './NavItem';
import ButtonOnlyIcon from '../Button/Only-icon';
import { Translator } from '../Language';
import { useSSO } from '../../context/SSO';
import UserInformation from './UserInformation';
import { crown } from '../../assets/new-images/icons';
import { defaultLogo } from '../../assets/new-images/logos';

// EG-27: paleta alinhada ao form (gs-form-survey-ui) p/ unificar o design.
const C = {
 bg: '#F1F0F6',
 line: '#ECEBF2',
 muted: '#8E8E9A',
 brand: '#0073BB',
};

// 272px (era 248px): "Templates e catálogos" tem 6 itens — quando aberto,
// o conteúdo passa da altura da sidebar e entra scrollbar; a largura extra
// dá folga pro texto não ficar espremido contra ela.
const SIDEBAR_W = '272px';

// Ícone + cor de destaque por grupo — dá identidade visual a cada seção
// (Cadastros/Templates/Operação já eram só texto plano) sem repetir o ícone
// de nenhum item de dentro do próprio grupo.
const GROUP_META = {
 Cadastros: { icon: FiFolderPlus, color: '#0073BB' },
 'Templates e catálogos': { icon: FiBookOpen, color: '#7C5CFC' },
 Operação: { icon: FiTrendingUp, color: '#0C8599' },
 Dashboards: { icon: FiBarChart2, color: '#B7791F' },
};

// MASTER/AVALIADOR/RESPONSAVEL → rótulo amigável pro badge de contexto.
function roleLabel({ hasMasterRole, hasAdminCompanyRole, hasEvaluatorRole }) {
 if (hasMasterRole) return 'Administrador';
 if (hasAdminCompanyRole) return 'Responsável';
 if (hasEvaluatorRole) return 'Avaliador';
 return null;
}

export default function Topbar() {
 const { user } = useSSO();
 const { isOpen, onOpen, onClose } = useDisclosure();

 const roles = user?.user?.roles || [];
 const hasMasterRole = roles.some((role) => role.name === 'MASTER');
 const hasAdminCompanyRole = roles.some((role) => role.name === 'RESPONSAVEL');
 const hasEvaluatorRole = roles.some((role) => role.name === 'AVALIADOR');

 // MASTER vê tudo menos "Revisão" (filtro por ref — slice fixo quebrava ao
 // adicionar itens, ex.: "Solicitações de acesso" e "Cronogramas"). AVALIADOR
 // (não-MASTER) vê "Auditoria" (EG-46) e "Cronogramas" (dos que é consultor
 // vinculado).
 const filteredLinkItems = hasMasterRole
  ? LinkItems.filter((item) => item.ref !== 'survey-analysis')
  : hasAdminCompanyRole
  ? [
     LinkItems.find((item) => item.name === 'Revisão'),
     // Sem `group`: RESPONSAVEL só tem esse item de dashboard — um grupo
     // colapsável de 1 item só obrigaria um clique extra à toa.
     { ...LinkItems.find((item) => item.ref === 'gri-dashboard'), group: undefined },
    ]
  : hasEvaluatorRole
  ? LinkItems.filter((item) => ['audit', 'cronogramas'].includes(item.ref))
  : [];

 const role = roleLabel({ hasMasterRole, hasAdminCompanyRole, hasEvaluatorRole });

 return (
  <Box w='100vw' minH='100vh' maxW='100%' overflowX='hidden' bg={C.bg}>
   {/* Sidebar fixa (desktop) */}
   <Box
    display={{ base: 'none', md: 'block' }}
    pos='fixed'
    w={SIDEBAR_W}
    h='full'
    zIndex={110}
   >
    <SidebarContent linkItems={filteredLinkItems} role={role} />
   </Box>

   {/* Sidebar (mobile, drawer) */}
   <Drawer
    isOpen={isOpen}
    placement='left'
    onClose={onClose}
    returnFocusOnClose={false}
    onOverlayClick={onClose}
   >
    <DrawerContent maxW={SIDEBAR_W}>
     <SidebarContent onClose={onClose} linkItems={filteredLinkItems} role={role} />
    </DrawerContent>
   </Drawer>

   <Box ml={{ base: 0, md: SIDEBAR_W }}>
    <Header onOpen={onOpen} />
    <Box
     p={{
      base: '21px 24px',
      xl: '32px',
      '2xl': '32px',
     }}
     minH='calc(100vh - 70px)'
    >
     <Outlet />
    </Box>
   </Box>
  </Box>
 );
}

// Agrupa preservando a ordem de 1ª aparição — item sem `group` fica solto na
// lista, no lugar onde apareceria de qualquer forma.
function groupLinkItems(linkItems) {
 const sections = [];
 const sectionByName = {};

 (linkItems || []).forEach((link) => {
  if (!link.group) {
   sections.push({ type: 'item', link });
   return;
  }
  let section = sectionByName[link.group];
  if (!section) {
   section = { type: 'group', title: link.group, items: [] };
   sectionByName[link.group] = section;
   sections.push(section);
  }
  section.items.push(link);
 });

 return sections;
}

const SidebarContent = ({ onClose, linkItems, role }) => {
 const { logout, user } = useSSO();
 const { pathname } = useLocation();

 const userEmail = user?.user?.email || '';
 const userName = user?.user?.username || '';

 const sections = useMemo(() => groupLinkItems(linkItems), [linkItems]);

 // "Dashboards" tem itens que compartilham prefixo de rota (/dashboards,
 // /dashboards/portfolio, ...). Um includes() ingênuo marcaria "Dashboard
 // Geral" (path=/dashboards) como ativo também em /dashboards/portfolio.
 // Resolve pelo prefixo MAIS LONGO que bate com a rota atual.
 const allNavPaths = useMemo(
  () =>
   (linkItems || [])
    .filter(Boolean)
    .map((item) => item.path),
  [linkItems]
 );
 const longestMatch = useMemo(() => {
  const matches = allNavPaths.filter(
   (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
  if (matches.length === 0) return null;
  return matches.reduce((longest, path) => (path.length > longest.length ? path : longest));
 }, [allNavPaths, pathname]);
 const isActive = (path) => path === longestMatch;

 // Accordion: só um grupo aberto por vez. Abre sozinho o grupo que contém a
 // rota atual, pra não esconder onde o usuário está.
 const activeGroupTitle = useMemo(
  () =>
   sections.find(
    (section) =>
     section.type === 'group' &&
     section.items.some((item) => pathname.includes(item.path))
   )?.title || null,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [sections]
 );
 const [openGroup, setOpenGroup] = useState(activeGroupTitle);

 function toggleGroup(title) {
  setOpenGroup((current) => (current === title ? null : title));
 }

 return (
  <Flex
   direction='column'
   h='full'
   bg='white'
   p='20px 14px'
   borderRight={`1px solid ${C.line}`}
   pos='relative'
  >
   {/* Friso da marca — a mesma pincelada que já aparece nos KPIs dos
       dashboards, só que aqui vertical, marcando o limite da sidebar. */}
   <Box
    pos='absolute'
    top='0'
    right='0'
    w='3px'
    h='full'
    bgGradient={`linear(to-b, ${C.brand}, #7C5CFC)`}
    opacity={0.5}
   />

   <Flex align='center' justify='space-between' px='10px' pb='14px'>
    <Image
     src={defaultLogo}
     alt='Geração Social'
     w='auto'
     h={['64px', '72px']}
    />
    <CloseButton
     display={{ base: 'flex', md: 'none' }}
     onClick={onClose}
     bg='gray.100'
     rounded='full'
    />
   </Flex>

   <UserInformation display={['flex', 'flex', 'none']} mx='6px' my='20px' />

   {role && (
    <Flex
     align='center'
     gap='7px'
     w='fit-content'
     mx='10px'
     mb='16px'
     px='10px'
     py='5px'
     borderRadius='full'
     bg='#EAF2FB'
    >
     <Box boxSize='6px' borderRadius='full' bg={C.brand} />
     <Text fontSize='11.5px' fontWeight={700} color={C.brand} letterSpacing='0.2px'>
      {role}
     </Text>
    </Flex>
   )}

   <Box overflowY='auto' flex='1' css={scrollbarCss}>
    {sections.map((section, index) =>
     section.type === 'item' ? (
      <NavItem
       key={`${section.link.name}_${index}`}
       icon={section.link.icon}
       name={section.link.name}
       path={section.link.path}
       active={isActive(section.link.path)}
       onClick={onClose}
      >
       {section.link.name}
      </NavItem>
     ) : (
      <NavGroup
       key={section.title}
       title={section.title}
       items={section.items}
       isOpen={openGroup === section.title}
       onToggle={() => toggleGroup(section.title)}
       onNavigate={onClose}
       isActive={isActive}
      />
     )
    )}
   </Box>

   <Box mt='10px' pt='12px' borderTop='1px solid #EDEDF2'>
    <Flex
     align='center'
     gap='10px'
     px='8px'
     py='8px'
     borderRadius='12px'
     transition='background-color 0.15s ease'
     _hover={{ bg: '#F7F8FA' }}
    >
     <Avatar
      name={userName}
      w='36px'
      h='36px'
      color='white'
      fontWeight={800}
      fontSize='13px'
      bgGradient='linear(135deg, #2E84C8, #0073BB)'
     />
     <Box flex='1' minW='0'>
      <Text fontSize='13px' fontWeight={700} color='#292B30' noOfLines={1}>
       {userName}
      </Text>
      <Text fontSize='11.5px' color={C.muted} noOfLines={1}>
       {userEmail}
      </Text>
     </Box>
     <IconButton
      aria-label='Sair'
      icon={<FiLogOut size={16} />}
      onClick={logout}
      size='sm'
      variant='ghost'
      color={C.muted}
      _hover={{ bg: '#FDECEC', color: '#C53030' }}
     />
    </Flex>
   </Box>
  </Flex>
 );
};

// Scrollbar fino em vez do padrão do sistema — só aparece no hover da área de
// navegação, pra não competir visualmente com os itens do menu.
const scrollbarCss = {
 '&::-webkit-scrollbar': { width: '5px' },
 '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '999px' },
 '&:hover::-webkit-scrollbar-thumb': { background: '#D8DCE3' },
};

// Título recolhível — envolve header + itens NUM CARTÃO SÓ quando aberto
// (fundo e borda na cor do grupo): sem isso, os itens abertos de um grupo
// ficavam soltos, sem nenhum limite visual separando de onde o grupo de cima
// termina — pareciam pertencer ao grupo errado. O cartão fecha esse contorno
// e o estado aberto/fechado fica evidente por 4 sinais ao mesmo tempo (fundo,
// borda, chip do ícone preenchido, chevron em selo), não só pela rotação sutil
// do chevron.
const NavGroup = ({ title, items, isOpen, onToggle, onNavigate, isActive }) => {
 const meta = GROUP_META[title] || { icon: FiActivity, color: '#0073BB' };
 const GroupIcon = meta.icon;

 return (
  <Box
   mb='10px'
   borderRadius='12px'
   bg={isOpen ? `${meta.color}0C` : 'transparent'}
   border='1px solid'
   borderColor={isOpen ? `${meta.color}2E` : 'transparent'}
   transition='background-color 0.2s ease, border-color 0.2s ease'
  >
   <Flex
    as='button'
    type='button'
    w='full'
    align='center'
    justify='space-between'
    px='10px'
    py='9px'
    borderRadius='11px'
    cursor='pointer'
    transition='background-color 0.15s ease'
    _hover={{ bg: isOpen ? `${meta.color}14` : '#F3F4F7' }}
    onClick={onToggle}
   >
    <Flex align='center' gap='10px'>
     <Flex
      align='center'
      justify='center'
      boxSize='26px'
      borderRadius='8px'
      bg={isOpen ? meta.color : `${meta.color}1A`}
      transition='background-color 0.2s ease'
      boxShadow={isOpen ? `0 2px 6px ${meta.color}4D` : 'none'}
     >
      <GroupIcon size={13} color={isOpen ? '#FFFFFF' : meta.color} />
     </Flex>
     <Text
      as='span'
      fontWeight={700}
      fontSize='13px'
      color={isOpen ? meta.color : '#5C6066'}
      letterSpacing='0.15px'
      lineHeight='normal'
     >
      {title}
     </Text>
    </Flex>
    <Flex
     align='center'
     justify='center'
     boxSize='20px'
     borderRadius='full'
     bg={isOpen ? `${meta.color}1F` : 'transparent'}
     transition='background-color 0.2s ease'
    >
     <Box
      as={FiChevronDown}
      boxSize='13px'
      color={isOpen ? meta.color : '#ADB3BC'}
      transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
      transition='transform 0.2s ease'
     />
    </Flex>
   </Flex>

   <Collapse in={isOpen} animateOpacity>
    {/* pl=0 no wrapper + pl=10 no item: o ícone do item cai a 24px da borda
        da sidebar, a MESMA posição do ícone do header do grupo (14 de
        padding da sidebar + 10). Antes o item entrava a 38px (14+8+16) —
        bem mais pra dentro que o header, parecia desalinhado/deslocado. */}
    <Box pl='0' pr='6px' pb='8px' pt='1px'>
     {items.map((item, index) => (
      <NavItem
       key={`${item.name}_${index}`}
       icon={item.icon}
       name={item.name}
       path={item.path}
       active={isActive(item.path)}
       onClick={onNavigate}
       pl='10px'
      >
       {item.name}
      </NavItem>
     ))}
    </Box>
   </Collapse>
  </Box>
 );
};

const Header = ({ onOpen }) => {
 return (
  <Flex
   as='header'
   h={['72px', '72px', '70px']}
   bg='white'
   borderBottom={`1px solid ${C.line}`}
   align='center'
   px={['20px', '24px', '28px']}
   gap='18px'
   pos='sticky'
   top='0'
   zIndex={100}
  >
   <Flex display={{ base: 'flex', md: 'none' }} align='center'>
    <LogoMobile logo={defaultLogo} />
   </Flex>

   <Flex ml='auto' align='center' gap='20px'>

    <Flex gap='12px' align='center' display={['none', 'flex']}>
     <Image src={crown} alt='Crown' height='22px' />
     <UserInformation flexDir='row-reverse' />
    </Flex>

    <ButtonOnlyIcon
     name='hamburger'
     onClick={onOpen}
     aria-label='open menu'
     display={{ base: 'flex', md: 'none' }}
    />
   </Flex>
  </Flex>
 );
};

// `group` agrupa o item sob um título recolhível na sidebar (accordion — só um
// grupo aberto por vez, ver SidebarContent). Item sem `group` fica solto,
// fora de qualquer seção — usado pros papéis com menu enxuto (RESPONSAVEL só
// vê "Revisão", por ex.) e pros itens de topo sem parentesco natural.
// `icon` casa com a chave do NAV_ICONS em NavItem.jsx (por convenção, igual
// ao `ref`) — cada item tem um ícone só seu, nenhum reaproveitado.
const LinkItems = [
 {
  name: 'Revisão',
  icon: 'survey-analysis',
  path: `/survey-analysis`,
  ref: 'survey-analysis',
 },
 {
  name: <Translator path={'company.sidebar'} />,
  icon: 'registration',
  path: `/registration`,
  ref: 'registration',
  group: 'Cadastros',
 },
 {
  name: 'Solicitações de acesso',
  icon: 'access-requests',
  path: `/access-requests`,
  ref: 'access-requests',
  group: 'Cadastros',
 },
 {
  name: 'Templates',
  icon: 'templates',
  path: `/templates`,
  ref: 'templates',
  group: 'Templates e catálogos',
 },
 {
  name: 'ESG Starter',
  icon: 'esg-starter',
  path: `/esg-starter`,
  ref: 'esg-starter',
  group: 'Templates e catálogos',
 },
 {
  name: 'Pilares',
  icon: 'pillars',
  path: `/pillars`,
  ref: 'pillars',
  group: 'Templates e catálogos',
 },
 {
  name: 'Temas',
  icon: 'themes',
  path: `/themes`,
  ref: 'themes',
  group: 'Templates e catálogos',
 },
 {
  name: 'Indicadores GRI',
  icon: 'gri-indicators',
  path: `/gri-indicators`,
  ref: 'gri-indicators',
  group: 'Templates e catálogos',
 },
 {
  name: 'Grupos de Comparação',
  icon: 'comparison-groups',
  path: `/comparison-groups`,
  ref: 'comparison-groups',
  group: 'Templates e catálogos',
 },
 {
  name: 'Questionários',
  icon: 'researches',
  path: `/researches`,
  ref: 'researches',
  group: 'Operação',
 },
 {
  name: 'Certificações',
  icon: 'certifications',
  path: `/certifications`,
  ref: 'certifications',
  group: 'Operação',
 },
 {
  name: 'Cronogramas',
  icon: 'cronogramas',
  path: `/cronogramas`,
  ref: 'cronogramas',
  group: 'Operação',
 },
 {
  name: 'Auditoria',
  icon: 'audit',
  path: `/audit`,
  ref: 'audit',
  group: 'Operação',
 },
 {
  name: 'Documentação',
  icon: 'documents',
  path: `/documents`,
  ref: 'documents',
  group: 'Operação',
 },
 {
  name: 'Aulas e Materiais',
  icon: 'educational-content',
  path: `/educational-content`,
  ref: 'educational-content',
  group: 'Operação',
 },
 {
  name: 'Dashboard Geral',
  icon: 'dashboards-geral',
  path: `/dashboards`,
  ref: 'dashboards-geral',
  group: 'Dashboards',
 },
 {
  name: 'Portfólio',
  icon: 'dashboards-portfolio',
  path: `/dashboards/portfolio`,
  ref: 'dashboards-portfolio',
  group: 'Dashboards',
 },
 {
  name: 'Fluxo de Certificação',
  icon: 'dashboards-fluxo',
  path: `/dashboards/fluxo`,
  ref: 'dashboards-fluxo',
  group: 'Dashboards',
 },
 {
  name: 'Histórico',
  icon: 'dashboards-historico',
  path: `/dashboards/historico`,
  ref: 'dashboards-historico',
  group: 'Dashboards',
 },
 {
  name: 'Por Empresa',
  icon: 'dashboards-por-empresa',
  path: `/dashboards/por-empresa`,
  ref: 'dashboards-por-empresa',
  group: 'Dashboards',
 },
 {
  name: 'Qualidade dos Dados',
  icon: 'dashboards-qualidade',
  path: `/dashboards/qualidade`,
  ref: 'dashboards-qualidade',
  group: 'Dashboards',
 },
 {
  name: 'Dashboard GRI',
  icon: 'gri-dashboard',
  path: `/gri-dashboard`,
  ref: 'gri-dashboard',
  group: 'Dashboards',
 },
];
